import * as DM from "@radix-ui/react-dropdown-menu";
import { cloneElement, isValidElement, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon, type IconName } from "../icons/Icon";
import { cx } from "../utils/format";

export interface MenuItem { id: string; label: string; icon?: IconName; /** a CSS colour for a leading dot (a tag's colour) */ dot?: string; /** a quiet note at the right end: a role, a shortcut */ meta?: string;
  /** the tag strip round (2026-10-24): a hover text for the entry (`title`), e.g. the investigation's level and credits; touch screens show none */ title?: string;
  /** the top bar round (2026-10-26): a smaller, lighter second line under the label (a status's "Our turn"); the entry's spoken name stays the label */ hint?: string;
  /** the composer-and-fixes round (2026-11-03, finding 22): a letter that picks the entry while the menu is open, shown at the right end and
   *  left out of the entry's spoken name (the reply bar's AI menu: P, I, O); Radix alone only moves the highlight on a letter */
  key?: string;
  checked?: boolean; disabled?: boolean; onSelect: () => void; }
export interface MenuSection { label?: string; items: MenuItem[]; }

const onPhone = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

/** `open` and `onOpenChange` make it controllable, for a menu a keyboard shortcut opens.
 *  On a phone the same sections open as a sheet from the bottom instead of a dropdown: a card that grows with its options up to
 *  most of the screen and scrolls inside, dismissed by a tap on the overlay, a pull on its grip, or Escape. */
export function Menu({ trigger, sections, align = "start", side, modal = true, open, onOpenChange, onCloseAutoFocus, tall }: { trigger: ReactNode; sections: MenuSection[]; align?: "start" | "end";
  /** false: the page behind stays readable and clickable while the menu is open (the reply bar's AI menu, opened by its key) */ modal?: boolean;
  /** the composer-and-fixes round (2026-11-03): "top" opens the menu above its trigger (the reply bar's AI menu); absent, below unless there is no room */ side?: "top" | "bottom";
  open?: boolean; onOpenChange?: (open: boolean) => void;
  /** builder v2: called when the closed menu is about to give the focus back to its trigger; `preventDefault()` keeps it where a pick put it */ onCloseAutoFocus?: (e: Event) => void;
  /** the status menu round (2026-11-02): a taller box (up to the room it has, at most about 640 px) for a menu whose last entries matter;
   *  every other menu keeps the 440 px box */ tall?: boolean }) {
  const [phone, setPhone] = useState(onPhone);
  useEffect(() => { const m = window.matchMedia("(max-width: 767px)"); const on = () => setPhone(m.matches); m.addEventListener("change", on); return () => m.removeEventListener("change", on); }, []);
  const [own, setOwn] = useState(false);
  const isOpen = open ?? own;
  const setOpen = (v: boolean) => { setOwn(v); onOpenChange?.(v); };
  if (phone) return <SheetMenu trigger={trigger} sections={sections} open={open} onOpenChange={onOpenChange} />;
  const pickByKey = (e: React.KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey || e.key.length !== 1) return;
    const it = keyedItem(sections, e.key); if (!it) return;
    e.preventDefault(); setOpen(false); it.onSelect();
  };
  return (
    <DM.Root open={isOpen} onOpenChange={setOpen} modal={modal}>
      <DM.Trigger asChild>{trigger}</DM.Trigger>
      <DM.Portal>
        {/* React events bubble through the portal to whatever holds the trigger: a click on an item must not also count as a click on the table row underneath */}
        <DM.Content className={cx("ob-menu", tall && "ob-menu--tall")} sideOffset={6} side={side} align={align} onClick={(e) => e.stopPropagation()} onKeyDown={pickByKey} onCloseAutoFocus={onCloseAutoFocus}>
          {sections.map((s, i) => (
            <div key={i}>
              {i > 0 && <DM.Separator className="ob-menu__sep" />}
              {s.label && <DM.Label className="ob-menu__label">{s.label}</DM.Label>}
              {s.items.map((it) => (
                <DM.Item key={it.id} className={cx("ob-menu__item", it.checked && "ob-menu__item--checked")} title={it.title} disabled={it.disabled} onSelect={it.onSelect} aria-label={it.hint ? it.label : undefined}>
                  {it.icon && <Icon name={it.icon} size="sm" />}
                  {it.dot && <span className="ob-menu__dot" style={{ background: it.dot }} aria-hidden="true" />}
                  {it.hint ? <span className="ob-menu__item-text">{it.label}<span className="ob-menu__item-hint">{it.hint}</span></span> : it.label}
                  {it.meta && <span className="ob-menu__meta">{it.meta}</span>}
                  {it.key && <kbd className="ob-menu__key" aria-hidden="true">{it.key}</kbd>}
                  {it.checked && <Icon name="check" size="sm" className="ob-menu__check" />}
                </DM.Item>
              ))}
            </div>
          ))}
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

/** the enabled entry whose `key` is the typed letter (any case), if one */
const keyedItem = (sections: MenuSection[], letter: string): MenuItem | undefined =>
  sections.flatMap((s) => s.items).find((it) => !it.disabled && it.key?.toLowerCase() === letter.toLowerCase());

/** The phone's menu: the trigger opens a sheet from the bottom with the same sections. Uncontrolled unless `open` is given. */
function SheetMenu({ trigger, sections, open, onOpenChange }: { trigger: ReactNode; sections: MenuSection[]; open?: boolean; onOpenChange?: (open: boolean) => void }) {
  const [own, setOwn] = useState(false);
  const isOpen = open ?? own;
  const setOpen = (v: boolean) => { setOwn(v); onOpenChange?.(v); };
  const t = isValidElement(trigger) ? cloneElement(trigger as ReactElement<{ onClick?: (e: React.MouseEvent) => void; "aria-haspopup"?: string; "aria-expanded"?: boolean }>, { onClick: (e: React.MouseEvent) => { e.stopPropagation(); setOpen(!isOpen); }, "aria-haspopup": "dialog", "aria-expanded": isOpen }) : trigger;
  return (
    <>
      {t}
      {isOpen && <Sheet onClose={() => setOpen(false)}>
        {sections.map((s, i) => (
          <div key={i} className="ob-sheet__section">
            {s.label && <div className="ob-menu__label">{s.label}</div>}
            {s.items.map((it) => (
              <button key={it.id} type="button" role="menuitem" className={cx("ob-menu__item", it.checked && "ob-menu__item--checked")} title={it.title} disabled={it.disabled} aria-label={it.hint ? it.label : undefined} onClick={(e) => { e.stopPropagation(); setOpen(false); it.onSelect(); }}>
                {it.icon && <Icon name={it.icon} size="sm" />}
                {it.dot && <span className="ob-menu__dot" style={{ background: it.dot }} aria-hidden="true" />}
                {it.hint ? <span className="ob-menu__item-text">{it.label}<span className="ob-menu__item-hint">{it.hint}</span></span> : it.label}
                {it.meta && <span className="ob-menu__meta">{it.meta}</span>}
                {it.checked && <Icon name="check" size="sm" className="ob-menu__check" />}
              </button>
            ))}
          </div>
        ))}
      </Sheet>}
    </>
  );
}

/** A card from the bottom of a phone's screen. It grows with its content up to 80 % of the screen, then scrolls inside; the grip stays.
 *  Closed by a tap on the overlay, Escape, or a pull down anywhere on the card, as on iOS: on the grip always, on the body only while
 *  its content is scrolled to the top (a pull on scrolled content scrolls it). */
export function Sheet({ children, onClose, label = "Options" }: { children: ReactNode; onClose: () => void; label?: string }) {
  const card = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const drag = useRef<{ y: number; at: number; live: boolean; /** the last move went down: a pull that came back up does not close */ down: boolean } | null>(null);
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    card.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => { window.removeEventListener("keydown", onKey, true); before?.focus?.(); };
  }, [onClose]);
  const start = (e: React.PointerEvent, onGrip: boolean) => { drag.current = { y: e.clientY, at: 0, live: onGrip || (body.current?.scrollTop ?? 0) <= 0, down: false }; };
  const move = (e: React.PointerEvent) => {
    const d = drag.current; if (!d || !card.current) return;
    const dy = e.clientY - d.y;
    if (!d.live) { if ((body.current?.scrollTop ?? 0) <= 0 && dy > 0) d.live = true; else return; } // content at the top, finger going down: the pull takes over from the scroll
    if (dy < 0) return;
    d.down = dy >= d.at; d.at = dy; card.current.style.transform = `translateY(${dy}px)`; card.current.style.transition = "none";
    if (e.currentTarget.hasPointerCapture?.(e.pointerId) === false) (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const end = () => { const d = drag.current; if (!d || !card.current) return; drag.current = null; card.current.style.transform = ""; card.current.style.transition = ""; if (d.at > 80 && d.down) onClose(); };
  return createPortal(
    <div className="ob-sheet__backdrop" role="presentation" onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      <div ref={card} className="ob-sheet" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => start(e, !!(e.target as HTMLElement).closest(".ob-sheet__grip"))} onPointerMove={move} onPointerUp={end} onPointerCancel={end}>
        <div className="ob-sheet__grip"><span aria-hidden="true" /></div>
        <div ref={body} className="ob-sheet__body">{children}</div>
      </div>
    </div>, document.body);
}

/** Label-over-value control that opens a menu (priority, assignee, status). */
export interface FieldProps extends ButtonHTMLAttributes<HTMLButtonElement> { label: string; value: string; icon?: IconName; avatar?: ReactNode;
  /** the top bar round (2026-10-26): a small red "!" on the top right corner; the word is added to the spoken name ("Status: Open, urgent") */ badge?: string;
  /** the status menu round (2026-11-02): more of the value after it (" · Tue 09:00"), part of the spoken name; a narrow ticket band hides it */ tail?: string; }

/** Chip that opens a menu (priority, assignee, status): a small icon or avatar, the value, a chevron. No label, no tooltip; the menu's section label names the field.
 *  Radix attaches its trigger handlers via props, so everything unknown is spread onto the button. */
export function Field({ label, value, icon, avatar, badge, tail, className, ...rest }: FieldProps) {
  return (
    <button type="button" className={cx("ob-field", badge && "ob-field--badged", className)} aria-label={`${label}: ${value}${tail ?? ""}${badge ? `, ${badge}` : ""}`} {...rest}>
      {avatar}{icon && <Icon name={icon} size="sm" />}<span className="ob-field__value">{value}{tail && <span className="ob-field__tail">{tail}</span>}</span><Icon name="chevronDown" size="sm" className="ob-field__chevron" />
      {badge && <span className="ob-field__badge" aria-hidden="true">!</span>}
    </button>
  );
}
